import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { ChevronLeft, Activity } from "lucide-react";

const CONFIG = {
  panic: {
    endpoint: "/panic-analytics",
    titleKey: "panic_analytics_title", subKey: "panic_analytics_sub",
    beforeKey: "intensity_before", afterKey: "intensity_after", techField: "technique_id",
    sections: [
      { freq: "stage_key", prefix: "analytics_stage_", titleKey: "analytics_sect_situations" },
      { freq: "technique_id", prefix: "analytics_tech_", titleKey: "analytics_sect_techniques" },
    ],
    tagField: "stage_key", tagPrefix: "analytics_stage_",
  },
  copd: {
    endpoint: "/copd-analytics",
    titleKey: "copd_analytics_title", subKey: "copd_analytics_sub",
    beforeKey: "difficulty_before", afterKey: "difficulty_after", techField: "technique_key",
    sections: [
      { freq: "technique_key", prefix: "analytics_tech_", titleKey: "analytics_sect_techniques" },
      { freq: "posture_key", prefix: "analytics_posture_", titleKey: "analytics_sect_postures" },
    ],
    tagField: "posture_key", tagPrefix: "analytics_posture_",
  },
};

function FreqBars({ freq, prefix, t }) {
  const entries = Object.entries(freq || {});
  const total = entries.reduce((a, [, n]) => a + n, 0);
  if (!total) return <p className="text-xs italic text-muted-foreground">{t("analytics_no_data")}</p>;
  return (
    <div className="space-y-2">
      {entries.map(([k, n]) => (
        <div key={k} className="flex items-center gap-3">
          <span className="w-28 truncate text-xs text-foreground">{t(prefix + k)}</span>
          <span className="h-2 flex-1 overflow-hidden rounded-full bg-secondary/60"><span className="block h-2 rounded-full bg-primary" style={{ width: `${(n / total) * 100}%` }} /></span>
          <span className="w-6 text-right text-[11px] font-medium text-muted-foreground">{n}</span>
        </div>
      ))}
    </div>
  );
}

export default function BreathingAnalytics({ kind, backTo }) {
  const cfg = CONFIG[kind];
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  usePageMeta(t(cfg.titleKey));

  useEffect(() => {
    api.get(cfg.endpoint).then((r) => setData(r.data)).catch(() => setData(null));
  }, [cfg.endpoint]);

  const summary = data?.summary;
  const back = () => navigate(backTo || -1);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid={`${kind}-analytics-screen`}>
      <div className="flex items-center gap-3">
        <button onClick={back} data-testid={`${kind}-analytics-back`} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-2xl text-foreground">{t(cfg.titleKey)}</h1>
      </div>

      {data === undefined ? <Spinner label={t("common.loading")} /> : !summary || summary.count === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Activity className="h-10 w-10 text-muted-foreground" />
          <p className="font-display text-lg text-foreground">{t("analytics_no_sessions")}</p>
          <p className="max-w-xs text-sm text-muted-foreground">{t("analytics_no_sessions_sub")}</p>
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">{t(cfg.subKey)}</p>

          <div className="grid grid-cols-3 gap-3">
            {[["analytics_kpi_sessions", String(summary.count)], ["analytics_kpi_avg_before", summary.avg_before ?? "—"], ["analytics_kpi_avg_after", summary.avg_after ?? "—"]].map(([lbl, val]) => (
              <div key={lbl} className="rounded-2xl border border-border bg-card p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t(lbl)}</p>
                <p className="mt-1 font-display text-xl text-foreground">{val}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-primary/40 bg-primary/5 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("analytics_kpi_reduction")}</p>
            <p className="mt-1 font-display text-3xl text-primary">−{summary.avg_reduction ?? "—"}</p>
          </div>

          <section className="space-y-2">
            <h2 className="font-display text-base text-foreground">{t("analytics_sect_trend")}</h2>
            <div className="flex h-28 items-end gap-1.5 rounded-2xl border border-border bg-card p-3">
              {(summary.weekly_trend || []).map((w, i) => {
                const h = w.avg_after != null ? Math.max(8, (w.avg_after / 10) * 80) : 4;
                return (
                  <div key={i} className="flex flex-1 flex-col items-center gap-1">
                    <span className="w-1.5 rounded-full" style={{ height: h, backgroundColor: w.avg_after != null ? "hsl(var(--primary))" : "hsl(var(--border))" }} />
                    <span className="text-[8px] text-muted-foreground">{w.week_start.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </section>

          {cfg.sections.map((sec) => (
            <section key={sec.freq} className="space-y-2">
              <h2 className="font-display text-base text-foreground">{t(sec.titleKey)}</h2>
              <FreqBars freq={summary.frequencies?.[sec.freq]} prefix={sec.prefix} t={t} />
            </section>
          ))}

          <section className="space-y-2">
            <h2 className="font-display text-base text-foreground">{t("analytics_sect_timeline")}</h2>
            <div className="space-y-2">
              {(data.timeline || []).slice(0, 15).map((row) => (
                <div key={row.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`${kind}-timeline-row`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{new Date(row.created_at).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                    <p className="font-display text-sm text-foreground">{t("analytics_tech_" + (row[cfg.techField] || ""))}</p>
                    {row[cfg.tagField] ? <p className="text-xs text-muted-foreground">{t(cfg.tagPrefix + row[cfg.tagField])}</p> : null}
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <p className="font-display text-sm text-primary">{t("analytics_before_short")} {row[cfg.beforeKey]} → {row[cfg.afterKey]}</p>
                    <p className="text-[10px] text-muted-foreground">{Math.round(row.duration_seconds)}{t("analytics_sec")}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
